import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const TimelineColumn = ({ 
  period, 
  actions, 
  onAddAction, 
  onEditAction, 
  onDeleteAction,
  onUpdateProgress 
}) => {
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical': return 'bg-error/10 text-error border-error/20';
      case 'high': return 'bg-warning/10 text-warning border-warning/20';
      case 'medium': return 'bg-accent/10 text-accent-foreground border-accent/20';
      default: return 'bg-muted text-muted-foreground border-border';
    }
  };

  const getProgressColor = (progress) => {
    if (progress >= 75) return 'bg-success';
    if (progress >= 50) return 'bg-accent';
    if (progress >= 25) return 'bg-warning';
    return 'bg-error';
  };

  return (
    <div className="flex flex-col h-full">
      <div className="bg-primary/5 rounded-t-xl p-4 md:p-6 border-b-2 border-primary">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg md:text-xl font-semibold text-primary">
            {period} Hari
          </h3>
          <span className="px-3 py-1 bg-primary/10 rounded-full text-sm font-medium text-primary">
            {actions?.length} Aksi
          </span>
        </div>
        <p className="text-sm text-muted-foreground">
          Target implementasi periode {period} hari
        </p>
      </div>
      <div className="flex-1 p-4 md:p-6 space-y-4 overflow-y-auto bg-card rounded-b-xl border border-t-0 border-border">
        {actions?.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
              <Icon name="Calendar" size={32} className="text-muted-foreground" />
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              Belum ada aksi untuk periode ini
            </p>
            <Button 
              variant="outline" 
              size="sm"
              iconName="Plus"
              iconPosition="left"
              onClick={() => onAddAction(period)}
            >
              Tambah Aksi
            </Button>
          </div>
        ) : (
          <>
            {actions?.map((action) => (
              <div 
                key={action?.id}
                className="bg-background rounded-lg border border-border p-4 hover:shadow-md transition-all duration-250 group"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`px-2 py-1 rounded text-xs font-medium border ${getPriorityColor(action?.priority)}`}>
                        {action?.priority === 'critical' ? 'Kritis' : 
                         action?.priority === 'high' ? 'Tinggi' : 
                         action?.priority === 'medium' ? 'Sedang' : 'Rendah'}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {action?.category}
                      </span>
                    </div>
                    <h4 className="font-semibold text-foreground mb-1 line-clamp-2">
                      {action?.title}
                    </h4>
                    <p className="text-sm text-muted-foreground line-clamp-2">
                      {action?.description}
                    </p>
                  </div>
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity ml-2">
                    <button
                      onClick={() => onEditAction(action)}
                      className="p-1.5 hover:bg-muted rounded transition-colors"
                      aria-label="Edit action"
                    >
                      <Icon name="Edit2" size={16} className="text-muted-foreground" />
                    </button>
                    <button
                      onClick={() => onDeleteAction(action?.id)}
                      className="p-1.5 hover:bg-error/10 rounded transition-colors"
                      aria-label="Delete action"
                    >
                      <Icon name="Trash2" size={16} className="text-error" />
                    </button>
                  </div>
                </div>

                <div className="space-y-3 mb-3">
                  <div className="flex items-center gap-2 text-sm">
                    <Icon name="User" size={16} className="text-muted-foreground" />
                    <span className="text-muted-foreground">PIC:</span>
                    <span className="font-medium text-foreground">{action?.responsible}</span>
                  </div>

                  {action?.budget && (
                    <div className="flex items-center gap-2 text-sm">
                      <Icon name="DollarSign" size={16} className="text-muted-foreground" />
                      <span className="text-muted-foreground">Budget:</span>
                      <span className="font-medium text-foreground">
                        Rp {action?.budget?.toLocaleString('id-ID')}
                      </span>
                    </div>
                  )}

                  {action?.dependencies && action?.dependencies?.length > 0 && (
                    <div className="flex items-start gap-2 text-sm">
                      <Icon name="Link" size={16} className="text-muted-foreground mt-0.5" />
                      <div className="flex-1">
                        <span className="text-muted-foreground">Dependensi:</span>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {action?.dependencies?.map((dep, idx) => (
                            <span 
                              key={idx}
                              className="px-2 py-0.5 bg-muted rounded text-xs"
                            >
                              {dep}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Progress</span>
                    <span className="font-semibold text-foreground">{action?.progress}%</span>
                  </div>
                  <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                    <div 
                      className={`h-full transition-all duration-500 ${getProgressColor(action?.progress)}`}
                      style={{ width: `${action?.progress}%` }}
                    />
                  </div>
                  <div className="flex gap-2 pt-2">
                    <Button
                      variant="outline"
                      size="xs"
                      onClick={() => onUpdateProgress(action?.id, Math.max(0, action?.progress - 10))}
                      disabled={action?.progress === 0}
                    >
                      -10%
                    </Button>
                    <Button
                      variant="outline"
                      size="xs"
                      onClick={() => onUpdateProgress(action?.id, Math.min(100, action?.progress + 10))}
                      disabled={action?.progress === 100}
                    >
                      +10%
                    </Button>
                  </div>
                </div>

                {action?.kpis && action?.kpis?.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-border">
                    <div className="flex items-center gap-2 mb-2">
                      <Icon name="Target" size={14} className="text-muted-foreground" />
                      <span className="text-xs font-medium text-muted-foreground">KPI Target</span>
                    </div>
                    <div className="space-y-1">
                      {action?.kpis?.map((kpi, idx) => (
                        <div key={idx} className="text-xs text-foreground flex items-center gap-1">
                          <Icon name="CheckCircle2" size={12} className="text-success" />
                          <span>{kpi}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}

            <Button
              variant="outline"
              size="sm"
              fullWidth
              iconName="Plus"
              iconPosition="left"
              onClick={() => onAddAction(period)}
              className="mt-4"
            >
              Tambah Aksi Baru
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default TimelineColumn;